// src/components/common/PhotoGallery.js
// Updated: 10/20/2025 09:35 AM (Added Debug Logging)

import React, { useState, useEffect, useMemo } from "react";
import { storage } from "../../firebase/config";
import { ref, listAll, getDownloadURL, uploadBytes, deleteObject } from "firebase/storage";
import { useFamily } from "../../context/FamilyContext";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { FaCamera, FaSpinner, FaTrash } from "react-icons/fa";
import Spinner from "./Spinner";

const MAX_UPLOAD_MB = 10;

function extractFamilyId(path) {
  if (!path) return null;
  const m = path.match(/^families\/([^/]+)/);
  return m?.[1] || null;
}

const PhotoGallery = ({
  storagePath,
  title = "Photo Gallery",
  emptyMessage = "No photos yet.\nBe the first to upload a photo!",
}) => {
  const { isHost } = useFamily();
  const { userData } = useAuth();
  const [imageRefs, setImageRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const familyIdFromPath = useMemo(() => extractFamilyId(storagePath), [storagePath]);
  const isReadyToList = Boolean(storagePath) && Boolean(userData?.uid) && Boolean(userData?.familyId);

  useEffect(() => {
    // --- ADDED LOG ---
    console.log('PhotoGallery useEffect:', {
        isReadyToList,
        storagePath,
        userId: userData?.uid,
        userFamilyId: userData?.familyId,
        familyIdFromPath,
        pathFamilyMatchesUserFamily: familyIdFromPath === userData?.familyId
    });
    // --- END LOG ---

    if (!isReadyToList) {
      setLoading(false);
      // Log why it's not ready
      console.log('PhotoGallery: Not ready to list yet.');
      return;
    }

    if (familyIdFromPath && userData.familyId && familyIdFromPath !== userData.familyId) {
      setLoading(false);
      console.warn(
        `PhotoGallery: storagePath familyId (${familyIdFromPath}) != user.familyId (${userData.familyId}). Skipping list to avoid rules 403.`
      );
      return;
    }

    const fetchImages = async () => {
      setLoading(true);
      const folder = storagePath.endsWith("/") ? storagePath : `${storagePath}/`;
      const galleryRef = ref(storage, folder);
      // --- ADDED LOG ---
      console.log('PhotoGallery: Attempting listAll on path:', galleryRef.fullPath);
      // --- END LOG ---

      try {
        const res = await listAll(galleryRef); //
        const refsAndUrls = await Promise.all(
          res.items.map(async (itemRef) => {
            const url = await getDownloadURL(itemRef);
            return { ref: itemRef, url };
          })
        );
        refsAndUrls.sort((a, b) => b.ref.name.localeCompare(a.ref.name));
        setImageRefs(refsAndUrls);
        // --- ADDED LOG ---
        console.log('PhotoGallery: Successfully listed items:', refsAndUrls.length);
        // --- END LOG ---
      } catch (error) {
        // Log exists here already
        console.error("Error fetching images:", error); //
        toast.error("Could not load photos.");
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [isReadyToList, storagePath, userData?.uid, userData?.familyId, familyIdFromPath]); // Added uid explicitly just in case

  // ... rest of the component (handleUpload, handleDelete, return JSX) remains the same ...

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!storagePath || !userData?.uid || !userData?.familyId) {
      toast.error("Please sign in and open a valid family gallery.");
      e.target.value = null;
      return;
    }

    if (familyIdFromPath && familyIdFromPath !== userData.familyId) {
      toast.error("You can only upload to your own family’s gallery.");
      e.target.value = null;
      return;
    }

    if (!file.type?.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      e.target.value = null;
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`Image must be <= ${MAX_UPLOAD_MB} MB.`);
      e.target.value = null;
      return;
    }

    setUploading(true);
    const folder = storagePath.endsWith("/") ? storagePath : `${storagePath}/`;
    const safeName = file.name.replace(/\s+/g, "_");
    const photoName = `${userData.uid}_${Date.now()}_${safeName}`;
    const photoRef = ref(storage, `${folder}${photoName}`);

    try {
      const snapshot = await uploadBytes(photoRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setImageRefs((prev) => [{ ref: snapshot.ref, url }, ...prev]);
      toast.success("Photo uploaded!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const handleDelete = async (photoRefToDelete) => {
    if (!isHost) {
      toast.error("Only hosts can delete photos.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this photo?")) return;

    try {
      await deleteObject(photoRefToDelete);
      setImageRefs((prev) => prev.filter((i) => i.ref.fullPath !== photoRefToDelete.fullPath));
      toast.success("Photo deleted!");
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Failed to delete photo.");
    }
  };

  const [emptyTitle, emptySubtitle] = emptyMessage.split("\n");

  return (
    <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
      <div className="flex justify-between items-center mb-4 gap-3">
        <h2 className="text-2xl font-semibold text-neutral-800">{title}</h2>
        {storagePath && (
          <label
            htmlFor="photo-upload"
            className={`cursor-pointer flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors ${
              uploading ? "opacity-50" : ""
            }`}
          >
            {uploading ? <FaSpinner className="animate-spin" /> : <FaCamera />}
            <span>Upload Photo</span>
          </label>
        )}
        <input
          id="photo-upload"
          type="file"
          className="sr-only"
          accept="image/*"
          onChange={handleUpload}
          disabled={uploading || !storagePath}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner size="md" />
        </div>
      ) : imageRefs.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {imageRefs.map((item, index) => (
            <div
              key={item.ref.fullPath}
              className="aspect-square bg-gray-200 rounded-md overflow-hidden relative group"
            >
              <img
                src={item.url}
                alt={`Gallery memory ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              {isHost && (
                <button
                  onClick={() => handleDelete(item.ref)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                  title="Delete Photo"
                >
                  <FaTrash size={10} />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FaCamera className="mx-auto text-5xl text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-800">{emptyTitle || "No photos yet."}</h3>
          <p className="text-gray-500 mt-2">
            {emptySubtitle || "Be the first to upload a photo!"}
          </p>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;